import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, FileStack } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";

const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  if (m < 60) return `${m} min ago`;
  return m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

export const WhatsNew = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/whats-new").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d || d.seen) return null;
  const r = d.release;
  const seen = () => api.post("/whats-new/seen", { version: r.version }).then(() => setD({ ...d, seen: true })).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="ax-panel p-4 mb-6 ax-fade border-primary" data-testid="home-whats-new">
      <div className="flex items-center gap-2 font-semibold mb-1"><Sparkles className="h-4 w-4 text-primary" />What's new in Axiom One
        <span className="text-xs font-normal text-muted-foreground" data-testid="home-whats-new-version">version {r.version}</span></div>
      <p className="text-sm mb-2" data-testid="home-whats-new-title">{r.title}</p>
      <ul className="text-sm list-disc pl-5 space-y-0.5 mb-3">{r.items.map((x) => <li key={x}>{x}</li>)}</ul>
      <button data-testid="home-whats-new-dismiss" onClick={seen} className="border h-8 px-3 text-xs hover:border-primary">Got it</button>
    </div>
  );
};

const UpdateRow = ({ u, base, onOpen }) => {
  const po = u.type === "po";
  const tag = po ? u.category_label : u.version > 1 ? `Version ${u.version}` : "New file";
  const tone = po ? "bg-sky-100 text-sky-800" : u.version > 1 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";
  const what = po ? [u.po_number && `PO ${u.po_number}`, u.po_date && `dated ${u.po_date}`].filter(Boolean).join(" · ") : `${u.category_label}: ${u.name}`;
  return (
    <Link to={`${base}/${u.uid}`} onClick={() => onOpen(u.update_id)} data-testid={`home-uid-update-${u.update_id}`}
      className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-2 border-t border-black/5 text-sm hover:text-primary">
      <span className="font-mono text-xs text-primary">{u.uid}</span>
      <span className="font-medium truncate flex-1 min-w-0">{u.client_name}</span>
      <span className={`text-xs px-2 rounded-full ${tone}`} data-testid={`home-uid-update-tag-${u.update_id}`}>{tag}</span>
      <span className="w-full text-xs text-muted-foreground truncate">{what ? `${what} · ` : ""}{u.by_name} · {ago(u.at)}</span>
    </Link>
  );
};

export const UidUpdates = () => {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const allowed = can("orders", "read") || can("operations", "read");
  const base = can("orders", "read") ? "/orders/uid" : "/ops/uid";
  const load = useCallback(() => api.get("/uid-updates").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { if (allowed) load(); }, [allowed, load]);
  if (!allowed || !rows?.length) return null;
  const seen = (ids) => api.post("/uid-updates/seen", { ids }).then(load).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-2xl border p-5 mb-6" style={{ background: "#fffaf0" }} data-testid="home-uid-updates">
      <div className="flex items-center gap-2 mb-2"><FileStack className="h-4 w-4 text-primary" /><span className="font-semibold">New on your UIDs</span>
        <span className="num text-sm text-muted-foreground" data-testid="home-uid-updates-count">{rows.length}</span>
        <button data-testid="home-uid-updates-seen-all" onClick={() => seen(null)} className="ml-auto text-xs text-primary hover:underline">Mark all seen</button></div>
      {rows.slice(0, 8).map((u) => <UpdateRow key={u.update_id} u={u} base={base} onOpen={(id) => seen([id])} />)}
    </div>
  );
};
